import { useState } from 'react'
import {
  usePublicCreator,
  usePublicCreatorSales,
  usePublicCreatorSeedings,
  usePublicCreatorTiers,
} from '@/hooks/use-public-creator'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { API_BASE_URL } from '@/lib/api'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import {
  AlertCircle,
  ArrowUpRight,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  ExternalLink,
  Instagram,
  Loader2,
  Lock,
  MessageCircle,
  Package,
  ShoppingBag,
  Sparkles,
  Star,
  TrendingUp,
  Truck,
} from 'lucide-react'
import { formatCurrency } from '@/lib/utils'
import { formatRelativeDate } from '@/lib/constants'
import type { PublicCreatorSale, PublicSeeding, PublicTier } from '@/lib/api/modules/public-creator'

interface CreatorPublicViewProps {
  token: string
}

const SALE_STATUS_MAP: Record<string, { label: string; className: string }> = {
  pending: { label: 'Pendente', className: 'bg-amber-500/15 text-amber-700 dark:text-amber-400' },
  approved: {
    label: 'Aprovada',
    className: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  },
  cancelled: {
    label: 'Cancelada',
    className: 'bg-red-500/15 text-red-700 dark:text-red-400',
  },
  paid: { label: 'Paga', className: 'bg-blue-500/15 text-blue-700 dark:text-blue-400' },
}

const SEEDING_STATUS_MAP: Record<string, { label: string; className: string }> = {
  pending: { label: 'Pendente', className: 'bg-muted text-muted-foreground' },
  processing: {
    label: 'Processando',
    className: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  },
  shipped: { label: 'Enviado', className: 'bg-blue-500/15 text-blue-700 dark:text-blue-400' },
  in_transit: {
    label: 'Em trânsito',
    className: 'bg-blue-500/15 text-blue-700 dark:text-blue-400',
  },
  delivered: {
    label: 'Entregue',
    className: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  },
  failed: { label: 'Falha', className: 'bg-red-500/15 text-red-700 dark:text-red-400' },
  returned: { label: 'Devolvido', className: 'bg-red-500/15 text-red-700 dark:text-red-400' },
}

type Tab = 'overview' | 'sales' | 'seedings'

export function CreatorPublicView({ token }: CreatorPublicViewProps) {
  const { data: creator, isLoading, error } = usePublicCreator(token)
  const [activeTab, setActiveTab] = useState<Tab>('overview')

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground font-medium">Carregando...</p>
        </div>
      </div>
    )
  }

  if (error || !creator) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background p-5">
        <div className="max-w-xs text-center space-y-4">
          <div className="h-14 w-14 rounded-xl bg-destructive/10 flex items-center justify-center mx-auto">
            <AlertCircle className="h-6 w-6 text-destructive" />
          </div>
          <div className="space-y-1.5">
            <h2 className="text-lg font-semibold text-foreground">Acesso Inválido</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Não conseguimos encontrar os dados deste perfil. Verifique se o link está correto ou
              entre em contato com o suporte.
            </p>
          </div>
        </div>
      </div>
    )
  }

  const totalEarned = creator.availableBalance + creator.pendingBalance
  const initials = creator.name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()

  return (
    <div className="min-h-screen bg-background pb-16">
      <main className="px-4 pt-8 pb-4 max-w-lg mx-auto space-y-5">
        {/* ── HEADER ── */}
        <header className="flex items-center gap-4 animate-fade-in-up">
          <Avatar className="h-14 w-14 shrink-0">
            <AvatarImage
              src={`${API_BASE_URL}/api/v1/creators/${token}/avatar`}
              alt={creator.name}
            />
            <AvatarFallback className="text-sm bg-primary/10 text-primary font-bold font-display">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-foreground tracking-tight truncate">
              {creator.name}
            </h1>
            <div className="flex items-center flex-wrap gap-1.5 mt-1">
              {creator.niche && (
                <Badge variant="secondary" className="text-[10px] font-medium">
                  {creator.niche}
                </Badge>
              )}
              {creator.instagramUsername && (
                <a
                  href={`https://instagram.com/${creator.instagramUsername.replace('@', '')}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Instagram className="h-3 w-3" />@{creator.instagramUsername.replace('@', '')}
                  <ExternalLink className="h-2.5 w-2.5 opacity-50" />
                </a>
              )}
            </div>
          </div>
        </header>

        {/* ── BALANCE CARD ── */}
        <div className="rounded-xl bg-card border p-5 space-y-4 animate-fade-in-up stagger-1">
          {/* Approved */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
                Comissões Aprovadas
              </span>
            </div>
            <div className="text-kpi text-foreground" style={{ fontSize: '2.5rem' }}>
              {formatCurrency(creator.availableBalance)}
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">Prontas para repasse</p>
          </div>
        </div>

        {/* ── STATS ROW ── */}
        <div className="grid grid-cols-3 gap-3 animate-fade-in-up stagger-2">
          <div className="rounded-xl bg-card border p-4">
            <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider mb-2">
              Pendente
            </p>
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0">
                <Clock className="h-4 w-4 text-amber-500" />
              </div>
              <p className="font-display font-bold text-sm text-foreground leading-none tracking-tight">
                {formatCurrency(creator.pendingBalance)}
              </p>
            </div>
          </div>
          <div className="rounded-xl bg-card border p-4">
            <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider mb-2">
              Vendas · 30d
            </p>
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
                <ShoppingBag className="h-4 w-4 text-blue-500" />
              </div>
              <p className="font-display font-bold text-xl text-foreground leading-none tracking-tight">
                {creator.totalSales30Days}
              </p>
            </div>
          </div>
          <div className="rounded-xl bg-card border p-4">
            <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider mb-2">
              Total Gerado
            </p>
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-xl bg-violet-500/10 flex items-center justify-center shrink-0">
                <TrendingUp className="h-4 w-4 text-violet-500" />
              </div>
              <p className="font-display font-bold text-sm text-foreground leading-none tracking-tight">
                {formatCurrency(totalEarned)}
              </p>
            </div>
          </div>
        </div>

        {/* ── TABS ── */}
        <div className="animate-fade-in-up stagger-3">
          <nav className="flex border-b">
            {[
              { key: 'overview' as Tab, label: 'Resumo' },
              { key: 'sales' as Tab, label: 'Comissões' },
              { key: 'seedings' as Tab, label: 'Envios' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 text-xs font-medium py-3 transition-colors relative ${
                  activeTab === tab.key
                    ? 'text-foreground'
                    : 'text-muted-foreground hover:text-foreground/70'
                }`}
              >
                {tab.label}
                {activeTab === tab.key && (
                  <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-primary rounded-full" />
                )}
              </button>
            ))}
          </nav>

          <div className="mt-4">
            {activeTab === 'overview' && <OverviewTab token={token} />}
            {activeTab === 'sales' && <SalesTab token={token} />}
            {activeTab === 'seedings' && <SeedingsTab token={token} />}
          </div>
        </div>

        {/* ── SUPPORT ── */}
        <div className="rounded-xl bg-card border p-5 space-y-3 animate-fade-in-up stagger-4">
          <div className="space-y-1">
            <h4 className="text-sm font-semibold text-foreground">Dúvidas sobre pagamentos?</h4>
            <p className="text-xs text-muted-foreground leading-relaxed">
              O repasse é feito mediante nota fiscal até a data do seu contrato. Entre em contato
              com seu gestor para alinhar o faturamento.
            </p>
          </div>
          <Button
            className="w-full h-11 rounded-xl gap-2"
            onClick={() => window.open('https://wa.me/')}
          >
            <MessageCircle className="h-4 w-4" />
            Chamar Suporte no WhatsApp
            <ArrowUpRight className="h-3.5 w-3.5 ml-auto opacity-70" />
          </Button>
        </div>

        {/* ── FOOTER ── */}
        <footer className="pt-4 pb-2 text-center">
          <p className="text-[10px] text-muted-foreground/50 font-medium">
            Powered by Rafobase · Tempo real
          </p>
        </footer>
      </main>
    </div>
  )
}

// ──────────────────────────────────────────────────────
// TAB PANELS
// ──────────────────────────────────────────────────────

function OverviewTab({ token }: { token: string }) {
  const { data: creator } = usePublicCreator(token)
  const { data: tiers, isLoading: tiersLoading } = usePublicCreatorTiers(token)

  if (tiersLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const currentTier = creator?.commissionTier ?? null
  const tierOverride = creator?.tierOverride ?? false
  const totalSales = creator?.approvedSales ?? 0
  const pendingSales = creator?.pendingSales ?? 0
  const sortedTiers = (tiers ?? []).slice().sort((a, b) => a.position - b.position)

  // Tiers the creator has already reached (minSalesCount <= totalSales)
  // Current tier is the one assigned by the system (could be override or earned)
  const nextTier = currentTier
    ? (sortedTiers.find((t) => t.position > currentTier.position) ?? null)
    : (sortedTiers[0] ?? null)

  const isMaxTier =
    currentTier !== null &&
    sortedTiers.length > 0 &&
    currentTier.position === sortedTiers[sortedTiers.length - 1]?.position

  return (
    <div className="space-y-4">
      {/* ── CURRENT TIER CARD ── */}
      {currentTier ? (
        <CurrentTierCard tier={currentTier} isOverride={tierOverride} />
      ) : (
        <div className="rounded-xl bg-card border p-5 text-center space-y-2">
          <div className="h-10 w-10 rounded-xl bg-muted flex items-center justify-center mx-auto">
            <Star className="h-5 w-5 text-muted-foreground" />
          </div>
          <h4 className="text-sm font-semibold text-foreground">Sem tier ativo</h4>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Continue vendendo para atingir seu primeiro tier.
          </p>
        </div>
      )}

      {/* ── PROGRESS SECTION ── */}
      {!tierOverride && sortedTiers.length > 0 && (
        <TierProgress
          totalSales={totalSales}
          pendingSales={pendingSales}
          currentTier={currentTier}
          nextTier={nextTier}
          isMaxTier={isMaxTier}
        />
      )}

      {/* ── TIER LADDER ── */}
      {sortedTiers.length > 0 && (
        <TierLadder
          tiers={sortedTiers}
          currentTier={currentTier}
          totalSales={totalSales}
          isOverride={tierOverride}
        />
      )}
    </div>
  )
}

// ──────────────────────────────────────────────────────
// OVERVIEW SUB-COMPONENTS
// ──────────────────────────────────────────────────────

function CurrentTierCard({ tier, isOverride }: { tier: PublicTier; isOverride: boolean }) {
  return (
    <div className="rounded-xl bg-card border overflow-hidden">
      {/* Top accent bar */}
      <div className="h-1 bg-gradient-to-r from-emerald-400 to-emerald-600" />
      <div className="p-5 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-0.5">
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
              Seu Tier Atual
            </p>
            <h3 className="text-xl font-bold text-foreground tracking-tight">{tier.name}</h3>
          </div>
          {isOverride ? (
            <Badge className="bg-violet-500/10 text-violet-700 dark:text-violet-400 border-0 gap-1 shrink-0">
              <Lock className="h-2.5 w-2.5" />
              Tier fixo
            </Badge>
          ) : (
            <div className="h-9 w-9 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
              <Star className="h-4 w-4 text-emerald-600 dark:text-emerald-400 fill-emerald-500/40" />
            </div>
          )}
        </div>

        {/* Benefits chips */}
        <div className="flex flex-wrap gap-2">
          <div className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500/10 px-3 py-1.5">
            <TrendingUp className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
            <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-400">
              {(tier.commissionRate * 100).toFixed(0)}% de comissão
            </span>
          </div>
          {tier.monthlySeedings > 0 && (
            <div className="inline-flex items-center gap-1.5 rounded-lg bg-blue-500/10 px-3 py-1.5">
              <Package className="h-3 w-3 text-blue-600 dark:text-blue-400" />
              <span className="text-xs font-semibold text-blue-700 dark:text-blue-400">
                {tier.monthlySeedings} envio{tier.monthlySeedings > 1 ? 's' : ''}/mês
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function TierProgress({
  totalSales,
  pendingSales,
  currentTier,
  nextTier,
  isMaxTier,
}: {
  totalSales: number
  pendingSales: number
  currentTier: PublicTier | null
  nextTier: PublicTier | null
  isMaxTier: boolean
}) {
  if (isMaxTier) {
    return (
      <div className="rounded-xl bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-500/20 p-5 flex items-center gap-4">
        <div className="h-10 w-10 rounded-xl bg-emerald-500/15 flex items-center justify-center shrink-0">
          <Sparkles className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
        </div>
        <div>
          <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">
            Tier máximo atingido!
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">Você está no topo. Continue assim!</p>
        </div>
      </div>
    )
  }

  if (!nextTier) return null

  const fromSales = currentTier?.minSalesCount ?? 0
  const toSales = nextTier.minSalesCount
  const rangeSize = toSales - fromSales
  const progressWithinRange = Math.max(0, totalSales - fromSales)
  const progressPct = rangeSize > 0 ? Math.min(100, (progressWithinRange / rangeSize) * 100) : 0
  const remaining = Math.max(0, toSales - totalSales)

  return (
    <div className="rounded-xl bg-card border p-5 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-foreground">Progresso para {nextTier.name}</p>
        <p className="text-[10px] text-muted-foreground tabular-nums">
          {totalSales} / {toSales} vendas aprovadas
          {pendingSales > 0 && (
            <span className="text-amber-600 dark:text-amber-400">
              {' '}
              ({pendingSales} pendente{pendingSales > 1 ? 's' : ''})
            </span>
          )}
        </p>
      </div>

      {/* Custom styled progress bar */}
      <div className="space-y-1.5">
        <Progress
          value={progressPct}
          className="h-2.5 bg-muted [&>[data-slot=progress-indicator]]:bg-gradient-to-r [&>[data-slot=progress-indicator]]:from-emerald-400 [&>[data-slot=progress-indicator]]:to-emerald-600"
        />
        <p className="text-[11px] text-muted-foreground">
          {remaining > 0 ? (
            <>
              Faltam{' '}
              <span className="font-semibold text-foreground">
                {remaining} venda{remaining > 1 ? 's' : ''}
              </span>{' '}
              para alcançar o tier{' '}
              <span className="font-semibold text-foreground">{nextTier.name}</span>
            </>
          ) : (
            <span className="text-emerald-600 dark:text-emerald-400 font-medium">
              Parabéns — próximo tier desbloqueado!
            </span>
          )}
        </p>
        <p className="text-[10px] text-muted-foreground/70 mt-1">
          Apenas vendas aprovadas (após período de garantia) contam para progressão de tier.
        </p>
      </div>
    </div>
  )
}

function TierLadder({
  tiers,
  currentTier,
  totalSales,
  isOverride,
}: {
  tiers: PublicTier[]
  currentTier: PublicTier | null
  totalSales: number
  isOverride: boolean
}) {
  return (
    <div className="rounded-xl bg-card border p-5 space-y-1">
      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest mb-4">
        Todos os Tiers
      </p>

      <div className="space-y-0">
        {tiers.map((tier, index) => {
          const isAchieved = !isOverride && totalSales >= tier.minSalesCount
          const isCurrent = currentTier?.slug === tier.slug
          const isLast = index === tiers.length - 1

          return (
            <div key={tier.slug} className="flex gap-3">
              {/* Stepper column */}
              <div className="flex flex-col items-center">
                {/* Circle indicator */}
                <div
                  className={`
                    h-7 w-7 rounded-full flex items-center justify-center shrink-0 z-10
                    transition-all duration-200
                    ${
                      isCurrent
                        ? 'bg-emerald-600 ring-2 ring-emerald-500/30 ring-offset-2 ring-offset-card'
                        : isAchieved
                          ? 'bg-emerald-600'
                          : 'bg-muted border border-border'
                    }
                  `}
                >
                  {isAchieved || isCurrent ? (
                    <CheckCircle2 className="h-4 w-4 text-white" />
                  ) : (
                    <span
                      className={`text-[10px] font-bold ${
                        isAchieved ? 'text-white' : 'text-muted-foreground'
                      }`}
                    >
                      {index + 1}
                    </span>
                  )}
                </div>

                {/* Connector line */}
                {!isLast && (
                  <div
                    className={`w-0.5 flex-1 min-h-[1.75rem] my-0.5 rounded-full ${
                      isAchieved ? 'bg-emerald-500/40' : 'bg-border'
                    }`}
                  />
                )}
              </div>

              {/* Content column */}
              <div className={`pb-5 flex-1 min-w-0 ${isLast ? 'pb-0' : ''}`}>
                <div
                  className={`
                    rounded-xl p-3 border transition-colors
                    ${
                      isCurrent
                        ? 'bg-emerald-500/5 border-emerald-500/30'
                        : isAchieved
                          ? 'bg-muted/30 border-transparent'
                          : 'bg-transparent border-transparent'
                    }
                  `}
                >
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <p
                        className={`text-sm font-semibold truncate ${
                          isCurrent
                            ? 'text-emerald-700 dark:text-emerald-400'
                            : isAchieved
                              ? 'text-foreground'
                              : 'text-muted-foreground'
                        }`}
                      >
                        {tier.name}
                      </p>
                      {isCurrent && (
                        <Badge className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-0 text-[9px] px-1.5 py-0 h-4 shrink-0">
                          Atual
                        </Badge>
                      )}
                    </div>
                    <p className="text-[10px] text-muted-foreground shrink-0 tabular-nums">
                      {tier.minSalesCount === 0 ? 'Entrada' : `${tier.minSalesCount}+ vendas`}
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-x-3 gap-y-1">
                    <span
                      className={`text-[11px] font-medium ${
                        isCurrent || isAchieved ? 'text-foreground' : 'text-muted-foreground/60'
                      }`}
                    >
                      {(tier.commissionRate * 100).toFixed(0)}% comissão
                    </span>
                    {tier.monthlySeedings > 0 && (
                      <span
                        className={`text-[11px] font-medium ${
                          isCurrent || isAchieved ? 'text-foreground' : 'text-muted-foreground/60'
                        }`}
                      >
                        {tier.monthlySeedings} envio{tier.monthlySeedings > 1 ? 's' : ''}/mês
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function SalesTab({ token }: { token: string }) {
  const [page, setPage] = useState(1)
  const { data, isLoading } = usePublicCreatorSales(token, page, 8)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!data || data.data.length === 0) {
    return (
      <div className="rounded-xl bg-card border p-8 text-center space-y-2">
        <ShoppingBag className="h-8 w-8 text-muted-foreground/30 mx-auto" />
        <p className="text-sm text-muted-foreground">Nenhuma comissão registrada ainda.</p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {data.data.map((sale) => (
        <SaleRow key={sale.id} sale={sale} />
      ))}

      {/* Pagination */}
      {data.meta.totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-[10px] text-muted-foreground">
            Página {data.meta.page} de {data.meta.totalPages}
          </p>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              disabled={page >= data.meta.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

function SaleRow({ sale }: { sale: PublicCreatorSale }) {
  const status = SALE_STATUS_MAP[sale.status] ?? SALE_STATUS_MAP.pending

  return (
    <div className="rounded-xl bg-card border p-3.5 flex items-center gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-foreground truncate">#{sale.orderReference}</p>
          <Badge
            variant="outline"
            className={`text-[10px] px-1.5 py-0 shrink-0 border-0 ${status.className}`}
          >
            {status.label}
          </Badge>
        </div>
        <p className="text-[10px] text-muted-foreground mt-0.5">
          {formatRelativeDate(sale.createdAt)}
          {sale.couponUsed && <> · {sale.couponUsed}</>}
        </p>
      </div>
      <div className="text-right shrink-0">
        <p className="text-sm font-semibold text-foreground">
          {formatCurrency(sale.commissionAmount)}
        </p>
        <p className="text-[10px] text-muted-foreground">
          {(sale.commissionRate * 100).toFixed(0)}% de {formatCurrency(sale.orderAmount)}
        </p>
      </div>
    </div>
  )
}

function SeedingsTab({ token }: { token: string }) {
  const { data: seedings, isLoading } = usePublicCreatorSeedings(token)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!seedings || seedings.length === 0) {
    return (
      <div className="rounded-xl bg-card border p-8 text-center space-y-2">
        <Package className="h-8 w-8 text-muted-foreground/30 mx-auto" />
        <p className="text-sm text-muted-foreground">Nenhum envio registrado ainda.</p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {seedings.map((seeding) => (
        <SeedingRow key={seeding.id} seeding={seeding} />
      ))}
    </div>
  )
}

function SeedingRow({ seeding }: { seeding: PublicSeeding }) {
  const status = SEEDING_STATUS_MAP[seeding.status] ?? SEEDING_STATUS_MAP.pending
  const productNames = seeding.products.map((p) => `${p.quantity}x ${p.name}`).join(', ')
  const hasTracking = seeding.trackingCode || seeding.trackingUrl

  return (
    <div className="rounded-xl bg-card border p-3.5 space-y-2.5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground truncate">{productNames}</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            {formatRelativeDate(seeding.createdAt)}
          </p>
        </div>
        <Badge
          variant="outline"
          className={`text-[10px] px-1.5 py-0 shrink-0 border-0 ${status.className}`}
        >
          {status.label}
        </Badge>
      </div>

      {hasTracking && (
        <div className="flex items-center gap-2 bg-muted/50 rounded-lg px-3 py-2">
          <Truck className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          {seeding.trackingUrl ? (
            <a
              href={seeding.trackingUrl}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-primary hover:underline font-medium truncate"
            >
              {seeding.trackingCode || 'Rastrear envio'}
              <ExternalLink className="h-2.5 w-2.5 inline ml-1 opacity-60" />
            </a>
          ) : (
            <span className="text-xs text-muted-foreground font-mono truncate">
              {seeding.trackingCode}
            </span>
          )}
          {seeding.carrier && (
            <span className="text-[10px] text-muted-foreground ml-auto shrink-0">
              {seeding.carrier}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
